<template>
    <PillDropdown
        ref="dropdownRef"
        :id="id"
        :label="label"
        :summary="summaryText"
        :disabled="disabled"
        :block="block"
        :wide="wide"
        :align="align"
        :error="error"
        @open="onOpen"
        @close="onClose"
    >
        <div v-if="showSelectAll && multiple" class="ui-control__header">
            <span class="ui-control-group__title">{{ selectAllLabel }}</span>
            <button
                type="button"
                class="ui-control-select-all"
                :class="{ 'ui-control-select-all--active': allSelected }"
                :disabled="disabled || !flatOptions.length"
                @click="toggleSelectAll"
            >
                {{ allSelected ? 'Убрать все' : 'Выбрать все' }}
            </button>
        </div>

        <template v-if="groups.length">
            <div class="ui-control-groups" style="padding: 0.25rem 0.35rem">
                <div v-for="group in groups" :key="group.value ?? group.label" class="ui-control-group">
                    <div v-if="group.label" class="ui-control-group__header">
                        <span class="ui-control-group__title">{{ group.label }}</span>
                    </div>
                    <label
                        v-for="option in group.options"
                        :key="String(option.value)"
                        class="ui-control__option"
                    >
                        <input
                            :type="multiple ? 'checkbox' : 'radio'"
                            :name="multiple ? undefined : radioName"
                            :checked="isSelected(option.value)"
                            :disabled="isOptionDisabled(option)"
                            @change="toggleOption(option.value)"
                        />
                        <slot name="option" :option="option" :selected="isSelected(option.value)">
                            <span class="ui-control__option-name">{{ option.label }}</span>
                            <span v-if="option.badge" class="ui-control__badge">{{ option.badge }}</span>
                        </slot>
                    </label>
                </div>
            </div>
        </template>

        <template v-else>
            <label
                v-for="option in flatOptions"
                :key="String(option.value)"
                class="ui-control__option"
            >
                <input
                    :type="multiple ? 'checkbox' : 'radio'"
                    :name="multiple ? undefined : radioName"
                    :checked="isSelected(option.value)"
                    :disabled="isOptionDisabled(option)"
                    @change="toggleOption(option.value)"
                />
                <slot name="option" :option="option" :selected="isSelected(option.value)">
                    <span class="ui-control__option-name">{{ option.label }}</span>
                    <span v-if="option.badge" class="ui-control__badge">{{ option.badge }}</span>
                </slot>
            </label>
            <p
                v-if="!flatOptions.length"
                class="ui-control__option"
                style="cursor: default; color: #94a3b8"
            >
                {{ emptyText }}
            </p>
        </template>
    </PillDropdown>
</template>

<script setup>
import { computed, ref } from 'vue';
import PillDropdown from '@/components/ui/forms/PillDropdown.vue';

const props = defineProps({
    modelValue: { type: [Array, Number, String, Boolean], default: null },
    options: { type: Array, default: () => [] }, // { value, label, badge?, disabled? }
    groups: { type: Array, default: () => [] }, // { label, value?, options: [] }
    label: { type: String, default: '' },
    id: { type: String, default: undefined },
    multiple: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    block: { type: Boolean, default: false },
    wide: { type: Boolean, default: false },
    align: { type: String, default: 'right' },
    error: { type: String, default: '' },
    placeholder: { type: String, default: 'Выберите' },
    emptyText: { type: String, default: 'Нет вариантов' },
    showSelectAll: { type: Boolean, default: false },
    selectAllLabel: { type: String, default: '' },
    minSelected: { type: Number, default: 0 }, // for multi: prevent deselecting below N
    valueType: { type: String, default: 'auto' }, // auto | number | string
    summaryFn: { type: Function, default: null },
});

const emit = defineEmits(['update:modelValue', 'change']);

const dropdownRef = ref(null);
const pendingValues = ref([]);
const isOpen = ref(false);
const radioName = `ui-select-${Math.random().toString(36).slice(2, 9)}`;

const flatOptions = computed(() => {
    if (props.groups.length) {
        return props.groups.flatMap((group) => group.options || []);
    }
    return props.options;
});

function normalizeValue(value) {
    if (value == null || value === '') {
        return value;
    }

    if (props.valueType === 'number') {
        return Number(value);
    }

    if (props.valueType === 'string') {
        return String(value);
    }

    // auto
    if (typeof value === 'number') {
        return value;
    }

    const asNumber = Number(value);
    if (!Number.isNaN(asNumber) && String(asNumber) === String(value).trim()) {
        return asNumber;
    }

    return value;
}

function valuesEqual(a, b) {
    return normalizeValue(a) === normalizeValue(b);
}

function normalizeModelValue(value) {
    if (props.multiple) {
        const list = Array.isArray(value) ? value : value != null && value !== '' ? [value] : [];
        return list.map(normalizeValue);
    }

    if (value == null || value === '') {
        return [];
    }

    if (Array.isArray(value)) {
        return value.length ? [normalizeValue(value[0])] : [];
    }

    return [normalizeValue(value)];
}

const committedValues = computed(() => normalizeModelValue(props.modelValue));

const displayValues = computed(() => (isOpen.value ? pendingValues.value : committedValues.value));

function isSelected(value) {
    const normalized = normalizeValue(value);
    return displayValues.value.some((item) => valuesEqual(item, normalized));
}

function isOptionDisabled(option) {
    if (props.disabled || option.disabled) {
        return true;
    }

    if (
        props.multiple &&
        props.minSelected > 0 &&
        displayValues.value.length <= props.minSelected &&
        isSelected(option.value)
    ) {
        return true;
    }

    return false;
}

const allSelected = computed(() => {
    const enabled = flatOptions.value.filter((option) => !option.disabled);
    if (!enabled.length) {
        return false;
    }
    return enabled.every((option) => isSelected(option.value));
});

function formatDefaultSummary(values) {
    if (!values.length) {
        return props.placeholder;
    }

    if (values.length === 1) {
        const option = flatOptions.value.find((item) => valuesEqual(item.value, values[0]));
        return option?.label || '1 выбрано';
    }

    const names = flatOptions.value
        .filter((item) => values.some((value) => valuesEqual(item.value, value)))
        .map((item) => item.label);

    if (names.length <= 2) {
        return names.join(', ');
    }

    return `${values.length} выбрано`;
}

const summaryText = computed(() => {
    const values = displayValues.value;
    if (typeof props.summaryFn === 'function') {
        return props.summaryFn(values, flatOptions.value);
    }
    return formatDefaultSummary(values);
});

function arraysEqual(a, b) {
    if (a.length !== b.length) {
        return false;
    }
    return a.every((item) => b.some((other) => valuesEqual(item, other)));
}

function emitValue(values) {
    if (props.multiple) {
        const next = [...values];
        emit('update:modelValue', next);
        emit('change', next);
        return;
    }

    const next = values.length ? values[0] : null;
    emit('update:modelValue', next);
    emit('change', next);
}

function onOpen() {
    pendingValues.value = [...committedValues.value];
    isOpen.value = true;
}

function onClose() {
    if (!isOpen.value) {
        return;
    }

    isOpen.value = false;

    if (props.multiple && !arraysEqual(pendingValues.value, committedValues.value)) {
        emitValue(pendingValues.value);
    }
}

function toggleOption(value) {
    const normalized = normalizeValue(value);

    if (!props.multiple) {
        pendingValues.value = [normalized];
        isOpen.value = false;
        dropdownRef.value?.close();
        if (!arraysEqual(pendingValues.value, committedValues.value)) {
            emitValue(pendingValues.value);
        }
        return;
    }

    const current = [...pendingValues.value];
    const index = current.findIndex((item) => valuesEqual(item, normalized));

    if (index >= 0) {
        if (props.minSelected > 0 && current.length <= props.minSelected) {
            return;
        }
        current.splice(index, 1);
    } else {
        current.push(normalized);
    }

    pendingValues.value = current;
}

function toggleSelectAll() {
    if (allSelected.value) {
        const keep = props.minSelected > 0
            ? pendingValues.value.slice(0, props.minSelected)
            : [];
        pendingValues.value = keep;
        return;
    }

    pendingValues.value = flatOptions.value
        .filter((option) => !option.disabled)
        .map((option) => normalizeValue(option.value));
}
</script>
